// Player status and news (PLAYER_NEWS.md, scenario E4): one line per fact,
// mine first, never a wall of everyone's news. Filters: Mine, All, Injuries,
// Moves.
//
// One screen for a practice league and a real one; a real league reads
// `GET /news` (PLAN-2026-09-26-every-screen-and-crayon P4), forty at a time
// with Older for the rest. Drawn through the kit.
//
// BESIDE IT ON A DESKTOP, my roster this week (B-395): who of mine is on the
// injury report or on a bye, the out first, so the feed's width is not all
// a column of one-line stories.
//
// A STORY YOU CAN ACT ON (B-416): a free agent's row has Add, one on waivers
// Claim, as his Players row has them, and opens the same sheet; the
// positions narrow the feed as they narrow the pool.

import { valueOf, whenInZone, type NewsFilter, type NewsView } from '@fsp/domain';
import { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useNews, useReaderZone } from '../data/hooks';
import { useLeagueRules } from '../data/rules';
import { positionLabel, PositionPill, ProTeamCode, StatusBadge } from '../kit';
import { Button, Caption, Chips, Line, RowName, RowSub, Segments, Text, Title } from '../kit/ui';
import { ListScreen } from '../templates/ListScreen';
import { FromLeague } from './shared/FromLeague';
import { PlayerName, TeamName } from './shared/Names';
import { RosterReport } from './news/RosterReport';
import { RosterMoveSheet, type RosterMove } from './shared/RosterMoveSheet';
import { POOL_POSITIONS } from './shared/positions';
import { useRemembered } from '../shell/tabMemory';
import { leagueIdFromPath } from '../data/route';
import '../styles/screens/news.css';

const FILTERS: { id: NewsFilter; label: string }[] = [
  { id: 'mine', label: 'Mine' },
  { id: 'all', label: 'All' },
  { id: 'injuries', label: 'Injuries' },
  { id: 'moves', label: 'Moves' },
];

/** When a story was published, read now, by the rule every time here follows (`whenInZone`). */
export function whenLabel(iso: string, zone: string): string {
  return whenInZone(iso, zone, Date.now());
}

export function severityColor(severity: NewsView['severity']): string {
  return severity === 'out'
    ? 'var(--fsp-loss)'
    : severity === 'warn'
      ? 'var(--fsp-warn)'
      : 'var(--fsp-ink-muted)';
}

export function NewsScreen() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('filter');
  const filter: NewsFilter = FILTERS.some((f) => f.id === raw) ? (raw as NewsFilter) : 'mine';
  const leagueKey = leagueIdFromPath(useLocation().pathname) ?? 'none';
  // KEPT BY THE TAB (UX-39), as Players' are.
  const [positions, setPositions] = useRemembered<string[]>(`news:${leagueKey}:positions`, []);
  const feed = useNews(filter, positions);
  // THE LEAGUE'S OWN POSITIONS, as Players offers them (Q11): a league of
  // defensive players filters by DL, LB and DB, one with no kicker offers
  // none. The scoring table has a section for each position the roster
  // starts; football's list is the fallback while the rules are read.
  const own = valueOf(useLeagueRules())?.scoringTable.sections.map((s) => s.position) ?? [];
  const offered = own.length > 0 ? own : POOL_POSITIONS;
  const zone = useReaderZone();
  const [move, setMove] = useState<RosterMove | null>(null);

  const head = (
    <div className="fsp-news-head">
      <Line gap={3} wrap>
        <Title>News</Title>
        <Segments
          label="Which news"
          items={FILTERS}
          value={filter}
          onChange={(v) => setParams(v === 'mine' ? {} : { filter: v }, { replace: true })}
        />
      </Line>
      <div className="fsp-news-positions">
        <Chips
          label="Positions"
          options={offered.map((p) => ({ value: p, label: positionLabel(p) }))}
          value={positions}
          onChange={setPositions}
        />
      </div>
    </div>
  );

  return (
    <>
      <FromLeague snapshot={feed.snapshot}>
        {(items) => (
          <ListScreen
            filters={head}
            header={
              <>
                <Caption>
                  {filter === 'mine'
                    ? 'Your players'
                    : filter === 'all'
                      ? 'Everyone'
                      : filter === 'injuries'
                        ? 'Injury report'
                        : 'League moves'}
                </Caption>
                <Caption>
                  {/* A count of what is loaded, said as one while Older has more. */}
                  {feed.older !== null
                    ? `latest ${items.length}`
                    : `${items.length} ${items.length === 1 ? 'item' : 'items'}`}
                </Caption>
              </>
            }
            rows={items}
            getKey={(n) => n.id}
            estimateSize={64}
            side={<RosterReport />}
            empty={
              positions.length > 0
                ? 'No news at those positions.'
                : filter === 'mine'
                  ? 'Quiet week. No news on your roster.'
                  : 'Nothing here yet. News lands as the week goes on.'
            }
            footer={
              feed.older !== null || feed.loadingOlder ? (
                <div className="fsp-news-older">
                  <Button
                    size="xs"
                    variant="quiet"
                    disabled={feed.loadingOlder}
                    onClick={() => feed.older?.()}
                  >
                    {feed.loadingOlder ? 'Reading…' : 'Older'}
                  </Button>
                </div>
              ) : undefined
            }
            renderRow={(n) => <NewsRow n={n} zone={zone} onMove={setMove} />}
          />
        )}
      </FromLeague>
      <RosterMoveSheet move={move} showProjection onClose={() => setMove(null)} />
    </>
  );
}

/**
 * What a story's row offers (B-416): Add for a free agent, Claim for one on
 * waivers — Claimed once I have — as his Players row offers them; nothing
 * for anybody else's player or one of mine, whose row says whose.
 */
function newsAction(n: NewsView): { label: string; move: RosterMove; claimed: boolean } | null {
  if (n.action !== 'add' && n.action !== 'claim') return null;
  const claimId = n.inLeague?.kind === 'waivers' ? n.inLeague.myClaimId : null;
  return {
    label: claimId !== null ? 'Claimed' : n.action === 'add' ? 'Add' : 'Claim',
    move: { kind: n.action, playerId: n.player.id, claimId },
    claimed: claimId !== null,
  };
}

/** One story: whose, what, when and from where. News and My Team's news panel (B-316). */
export function NewsRow({
  n,
  zone,
  onMove,
}: {
  n: NewsView;
  zone: string | null;
  /** Opens the add or claim sheet; absent, the row offers nothing (B-416). */
  onMove?: (move: RosterMove) => void;
}) {
  const act = onMove === undefined ? null : newsAction(n);
  return (
    <div
      className={`fsp-row fsp-news-row${act !== null ? ' fsp-news-row-acts' : ''}`}
      data-severity={n.severity}
    >
      <PositionPill pos={n.player.positions[0] ?? '—'} size="sm" />
      <span className="fsp-news-dot" data-severity={n.severity} aria-hidden />
      <div className="fsp-news-body">
        <RowName
          marks={
            <>
              <ProTeamCode code={n.player.proTeam} />
              <StatusBadge player={n.player} />
              {n.mine ? <Caption>yours</Caption> : null}
            </>
          }
        >
          <PlayerName player={n.player} />
        </RowName>
        <Text block>
          {n.headline}
          {n.body ? <Text tone="secondary"> {n.body}</Text> : null}
        </Text>
        <RowSub>
          {zone === null ? n.source : `${whenLabel(n.publishedAt, zone)} · ${n.source}`}
          {n.team && !n.mine ? (
            <>
              {' · '}
              <TeamName team={n.team}>{n.team.ownerName}’s</TeamName>
            </>
          ) : null}
        </RowSub>
      </div>
      {act !== null ? (
        <div className="fsp-news-act">
          <Button
            size="xs"
            variant={
              act.claimed ? 'secondary' : act.move.kind === 'claim' ? 'caution-soft' : 'soft'
            }
            label={`${act.label} ${n.player.name}`}
            onClick={() => onMove!(act.move)}
          >
            {act.label}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
